<template>
  <transition name="c-actions-popover-slide" appear>
    <div
      v-click-outside="{ ev: 'mouseup', fn: onClickOutside }"
      ref="wrapper"
      class="c-actions-popover"
    >
      <c-popover
        v-bind="$attrs"
        :align="align"
        class="popover"
        :element-spacing="elementSpacing"
        :selector="scrollSelector"
        :target="target"
        :scroll-target="scrollTarget"
        :hide-on-scroll="shouldHideOnScroll"
        :position="calculatedPosition"
        @handler="$emit('close')"
        v-on="componentListeners"
      >
        <div
          :class="[
            'arrow',
            { '-hover': !!arrowHover },
            calculatedPosition, arrowColor,
            `-align-${align}`,
          ]"
        />

        <c-card class="actions" no-border no-hover>
          <slot>
            <div
              v-for="(item, index) in items"
              :key="index"
              :ref-cy="`logged-action-${item.key}`"
              :data-testid="`action-${item.name || item.label}`"
              :class="getClasses(item, index)"
              @click.prevent.stop="action(item)"
              @mouseover="setArrow(index)"
              @mouseout="arrowHover = false"
            >
              <c-icon
                v-if="item.icon"
                class="icon"
                :size="20"
                :icon="item.icon"
              />

              <slot name="action-content" :item="item" />

              <span
                class="text"
                :ref-cy="'popover-' + item.label"
              >
                {{ item.label }}
              </span>

            </div>
          </slot>
        </c-card>
      </c-popover>

      <c-overlay
        class="overlay"

        :show="isMobile"
        :transparent="!isMobile"

        @close.prevent.stop="e => $emit('close', e)"
      />
    </div>
  </transition>
</template>

<script>
import { MediaQuery } from '@convenia/mixins'
import { clickOutside } from '@convenia/components/directives'
import useVue3Migration from '@convenia/composables/useVue3Migration'
import { COMPONENT_HOOKS } from '@convenia/utils/modules/vue3Migration'
import CIcon from '@convenia/components/CIcon'
import CCard from '@convenia/components/CCard'
import CPopover from '@convenia/components/CPopover'
import COverlay from '@convenia/components/COverlay'

const ACTION_ROW_HEIGHT = 40

export default {
  name: 'CActionsPopover',

  mixins: [ MediaQuery ],

  components: {
    CIcon,
    CCard,
    CPopover,
    COverlay
  },

  directives: { clickOutside },

  props: {
    target: {
      type: [ Object, Element ],
      default: null,
    },
    scrollSelector: {
      type: String,
      default: 'body'
    },
    scrollTarget: {
      type: [ Object, Element ],
      default: () => {},
    },
    elementSpacing: {
      type: [ String, Number ],
      default: 5
    },
    items: {
      type: Array,
      default: () => [],
    },
    clickPosition: {
      type: [ Number, Object ],
      default: null,
    },
    height: {
      type: [ String, Number ],
      default: 0,
    },
    position: {
      type: String,
      default: 'bottom'
    },
    ignoreTargetClick: Boolean,

    /**
     * Closes the popover when the scroll target scrolls.
     * Ignored on mobile, where the popover renders as a fixed bottom sheet.
     */
    hideOnScroll: Boolean,

    /**
     * The horizontal alignment of the popover element relative to the target.
     */
    align: {
      type: String,
      default: 'left',
      validator: align => [ 'right', 'center', 'left' ].includes(align)
    },
  },

  emits: [
    'close',
  ],

  setup (_, ctx) {
    const { componentListeners } = useVue3Migration(ctx)

    return {
      componentListeners
    }
  },

  data: () => ({ arrowHover: false, arrowColor: 'red' }),

  methods: {
    action ({ action, name, props }) {
      typeof action === 'function' && action()
      name && this.$emit(`action:${name}`, props)
      this.$emit('close')
    },

    getClasses (item, index) {
      return [ 'action', {
        [`-border-${item.border}`]: item.border,
        [`-highlight-${item.highlight}`]: item.highlight,
        '-first': this.calculatedPosition === 'bottom' && index === 0,
        '-last': this.calculatedPosition === 'top' && index === this.items.length - 1
      } ]
    },

    setArrow (index) {
      const isFirst = index === 0 && this.calculatedPosition === 'bottom'
      const isLast = index === this.items.length - 1 && this.calculatedPosition === 'top'
      if (isFirst) {
        this.arrowHover = true
        this.arrowColor = this.items[0].highlight
      } else if (isLast) {
        this.arrowHover = true
        this.arrowColor = this.items[this.items.length - 1].highlight
      }
    },

    onClickOutside (e) {
      const target = (this.target || {}).$el || this.target

      if (this.ignoreTargetClick && target?.contains(e.target)) return
      this.$emit('close')
    },

    // Compensates offsetParent's top/left when an ancestor (eg. a CModal's transform)
    // traps `position: absolute`, keeping the mobile bottom sheet anchored to the viewport.
    updateMobileViewportAnchor () {
      const el = this.$refs.wrapper
      if (!el) return

      const { offsetParent } = el
      const { top, left } = offsetParent
        ? offsetParent.getBoundingClientRect()
        : { top: 0, left: 0 }

      el.style.top = `${-top}px`
      el.style.left = `${-left}px`
    },

    setupMobileViewportAnchor () {
      this.updateMobileViewportAnchor()
      window.addEventListener('resize', this.updateMobileViewportAnchor)
      document.body.addEventListener('scroll', this.updateMobileViewportAnchor, true)
    },

    teardownMobileViewportAnchor () {
      window.removeEventListener('resize', this.updateMobileViewportAnchor)
      document.body.removeEventListener('scroll', this.updateMobileViewportAnchor, true)
    }
  },

  watch: {
    isMobile (value) {
      value ? this.setupMobileViewportAnchor() : this.teardownMobileViewportAnchor()
    },
  },

  mounted () {
    if (this.isMobile)
      this.setupMobileViewportAnchor()
  },

  [COMPONENT_HOOKS.BEFORE_UNMOUNT] () {
    this.teardownMobileViewportAnchor()
  },

  computed: {
    calculatedPosition () {
      if (this.clickPosition)
        return (window.innerHeight - this.clickPosition > parseInt(this.height)) ? 'bottom' : 'top'

      const targetElement = (this.target || {}).$el || this.target
      if (!targetElement?.getBoundingClientRect) return this.position

      const { bottom } = targetElement.getBoundingClientRect()
      const requiredHeight = this.items.length * ACTION_ROW_HEIGHT + parseInt(this.elementSpacing)

      return (window.innerHeight - bottom) < requiredHeight ? 'top' : this.position
    },

    // On mobile the popover renders as a fixed bottom sheet, detached from
    // the target's position, so scrolling underlying content shouldn't close it.
    shouldHideOnScroll () {
      return !this.isMobile && this.hideOnScroll
    }
  }
}
</script>

<style lang="scss">
$border-color-hover: rgb(250, 250, 251);

%hover-arrow {
  z-index: $z-index-4;
  border-style: solid;
  border-top-color: transparent;
  border-left-color: transparent;
  border-right-color: transparent;
  border-bottom-color: transparent;

  transition: border-color .15s, background-color .15s;
}

.c-actions-popover {
  & > .overlay {
    display: unset;
    cursor: default;
  }

  @include responsive (xs-mobile, mobile) {
    // absolute (not fixed) so `updateMobileViewportAnchor` can read offsetParent
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100svh;

    z-index: var(--z-index-5);
  }

  & > .c-popover.popover {
    @include responsive (xs-mobile, mobile) {
      // absolute so bottom/max-height resolve against 100svh above, not the layout viewport
      position: absolute !important;
      bottom: 20px;
      z-index: var(--z-index-4);
      left: 50% !important;
      top: unset !important;
      transform: translateX(-50%);
      max-height: 60svh;
      overflow-y: auto;

      width: calc(100vw - 20px);

      &::before { display: none; }
    }

    &::before {
      box-shadow: none;
      display: none;
    }

    & > .arrow {
      position: absolute;
      content: '';
      width: 10px;
      height: 10px;
      border-radius: 4px 0 4px 0;
      background-color: #FFFFFF;
      transform: rotate(45deg);
      border-width: 5px;

      &.-align-left {
        left: auto;
        right: 15px;
      }

      &.-align-center {
        left: calc(50% - 5px);
        right: auto;
      }

      &.-align-right {
        left: 15px;
        right: auto;
      }

      &.bottom { top: -5px }
      &.top { bottom: -5px }

      @include responsive (xs-mobile, mobile) { display: none; }

      &.-hover {
        background-color: $border-color-hover;
        &.red { background-color: rgba(255, 121, 135, 0.2); }
      }
    }

    & > .actions {
      display: flex;
      flex-basis: 100%;
      flex-direction: column;
      min-width: 240px;

      overflow: hidden;

      background-color: transparent !important;
      background: linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.95) 100%) !important;
      box-shadow: unset;

      & > .action {
        display: flex;
        flex-basis: 50%;
        align-items: center;

        cursor: pointer;
        padding: 10px;
        border-bottom: none;
        background-color: transparent;
        color: color-var(text, base-80);

        transition: background-color .15s;

        &::before { @extend %hover-arrow; }
        &:hover { background-color: color-var(text, base-02); }
        &:only-child { border-radius: 5px; }

        @include responsive (xs-mobile, mobile) { &::before { display: none; } }

        &.-first {
          border-top-left-radius: 5px;
          border-top-right-radius: 5px;

          &::before { top: -5px; }
        }

        &.-last {
          border-bottom-left-radius: 5px;
          border-bottom-right-radius: 5px;

          &::before { bottom: -5px; }
        }

        &.-last:hover::before {
          border-color: color-var(text, base-02);
          background-color: color-var(text, base-02);

          border-bottom-color: $border-color-hover;
          border-right-color: $border-color-hover;
          border-bottom-left-radius: 5px;
          border-bottom-right-radius: 5px;
        }

        &.-highlight-red { color: color-var(negative); }
        &.-highlight-red > .icon { @include icon-color(negative); }
        &.-highlight-red:hover {
          background: rgba(255, 121, 135, 0.2);

          &.-first::before {
           border-color: rgba_to_rgb(rgba(255, 121, 135, 0.2));
          }
        }

        &.-border-top { border-top: var(--base-border) }
        &.-border-bottom { border-bottom: var(--base-border) }

        & > .text { padding-left: 10px; font-size: 14px; }
        & > .icon { @include icon-color(text, base-30) }

        @include responsive (xs-mobile, mobile) {
          padding: 20px;

          & > .text { padding-left: 20px }
        }
      }
    }
  }
}

@include responsive (xs-mobile, mobile) {
  .c-actions-popover-slide {
    &-enter-active,
    &-leave-active {
      transition: opacity .5s;

      & > .popover { transition: bottom .4s; }
    }

    &-enter,
    &-leave-to {
      opacity: 0;

      & > .popover { bottom: -100% !important; }
    }
  }
}
</style>
